var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Log } from '../../log';
import { Sutl } from '../../sutl';
import { DeferredMerge } from './DeferredUpdate';
export class DirectoryManager extends DeferredMerge {
    constructor() {
        super(5000);
        this.companyDirectory = {};
        this.equipmentDirectories = {};
        this.log = Log.channel('DirectoryManager');
        this.onCompanyDirectoriesChanged = this.onCompanyDirectoriesChanged.bind(this);
    }
    disable() {
        this.log.info(`disabling`);
        this.cancel();
        if (this.room === undefined) {
            this.log.error(`disable: room undefined`);
        }
        else {
            const companyDirectories = this.room.$getCompanyDirectories();
            companyDirectories.$offChildAdded(this.onCompanyDirectoriesChanged);
            companyDirectories.$offChildChanged(this.onCompanyDirectoriesChanged);
            companyDirectories.$offChildRemoved(this.onCompanyDirectoriesChanged);
        }
        this.companyDirectory = {};
        this.equipmentDirectories = {};
        this.room = undefined;
    }
    directoryEntriesMatch(entry1, entry2) {
        return this.getDirectoryEntryName(entry1) === this.getDirectoryEntryName(entry2);
    }
    doMerge(update, state) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            if (this.room === undefined)
                return;
            let start = Date.now();
            if (update.companyDirectoriesChanged || update.equipmentDirectories) {
                this.log.info(`starting directory merge`);
            }
            else {
                this.log.info(`updating directory entries`);
            }
            if (update.companyDirectoriesChanged) {
                const companyDirectories = this.room.$getCompanyDirectories();
                this.companyDirectory = {};
                yield companyDirectories.$loaded();
                if (state.cancelled) {
                    this.log.info(`cancelling directory merge`);
                    return;
                }
                companyDirectories
                    .map(dir => dir.$id)
                    .sort()
                    .forEach(dirId => {
                    var _a;
                    const dir = companyDirectories.$recordAt(dirId);
                    for (const [entryId, entry] of Object.entries((_a = dir === null || dir === void 0 ? void 0 : dir.entries) !== null && _a !== void 0 ? _a : {})) {
                        this.mergeEntryIntoDirectory(this.companyDirectory, entry, entryId);
                    }
                });
            }
            let equipmentDirectoriesChanged = false;
            if (update.equipmentDirectories) {
                const equipmentIds = Object.keys(update.equipmentDirectories).sort();
                for (const equipmentId of equipmentIds) {
                    const directory = update.equipmentDirectories[equipmentId];
                    if (directory) {
                        this.equipmentDirectories[equipmentId] = directory;
                        equipmentDirectoriesChanged = true;
                    }
                    else if (this.equipmentDirectories[equipmentId]) {
                        delete this.equipmentDirectories[equipmentId];
                        equipmentDirectoriesChanged = true;
                    }
                }
            }
            if (update.companyDirectoriesChanged || equipmentDirectoriesChanged) {
                if (update.equipmentDirectoryEntries) {
                    const equipmentIds = Object.keys(update.equipmentDirectoryEntries).sort();
                    for (const equipmentId of equipmentIds) {
                        const entries = (_a = update.equipmentDirectoryEntries[equipmentId]) !== null && _a !== void 0 ? _a : {};
                        const directory = this.equipmentDirectories[equipmentId];
                        if (!directory) {
                            this.log.error(`no directory for equipment id "${equipmentId}"`);
                            continue;
                        }
                        for (const [entryId, entry] of Object.entries(entries)) {
                            if (entry) {
                                directory[entryId] = entry;
                            }
                            else {
                                delete directory[entryId];
                            }
                        }
                    }
                }
                const roomDirectoryEntries = Sutl.deepCopy(this.companyDirectory);
                let n = 0;
                for (const directory of Object.values(this.equipmentDirectories)) {
                    for (const [entryId, entry] of Object.entries(directory)) {
                        this.mergeEntryIntoDirectory(roomDirectoryEntries, entry, entryId);
                        // Defer execution to other parts of the app to prevent lags when merging huge
                        // directories.
                        if (++n >= 10) {
                            n = 0;
                            yield this.timeouts.delay(1);
                            if (state.cancelled) {
                                this.log.info(`cancelling directory merge`);
                                return;
                            }
                        }
                    }
                }
                let elapsedSec = (0.001 * (Date.now() - start)).toFixed(1);
                this.log.info(`directory merge complete, elapsed time: ${elapsedSec} seconds`);
                this.log.info(`starting full directory update`);
                start = Date.now();
                if (this.room === undefined)
                    return;
                const roomDirectory = this.room.$getDirectory();
                yield roomDirectory.$setEntries(roomDirectoryEntries);
                elapsedSec = (0.001 * (Date.now() - start)).toFixed(1);
                this.log.info(`full directory update complete, elapsed time: ${elapsedSec} seconds`);
                if (state.cancelled)
                    return;
            }
            else if (update.equipmentDirectoryEntries) {
                if (this.room === undefined)
                    return;
                const roomDirectory = this.room.$getDirectory();
                yield roomDirectory.$loaded();
                if (state.cancelled) {
                    this.log.info(`cancelling directory entries update`);
                    return;
                }
                const equipmentIds = Object.keys(update.equipmentDirectoryEntries).sort();
                for (const equipmentId of equipmentIds) {
                    const entries = (_b = update.equipmentDirectoryEntries[equipmentId]) !== null && _b !== void 0 ? _b : {};
                    for (const [entryId, entry] of Object.entries(entries)) {
                        if (entry) {
                            const newEntryId = this.mergeEntryIntoDirectory(roomDirectory.entries, entry, entryId);
                            const newEntry = roomDirectory.entries[newEntryId];
                            yield roomDirectory.$setEntry(newEntryId, newEntry);
                        }
                        else {
                            yield roomDirectory.$setEntry(entryId, null);
                        }
                        if (state.cancelled) {
                            this.log.info(`cancelling directory entries update`);
                            return;
                        }
                    }
                }
                const elapsedSec = (0.001 * (Date.now() - start)).toFixed(1);
                this.log.info(`directory entries update complete, elapsed time: ${elapsedSec} seconds`);
            }
        });
    }
    enable(room) {
        return __awaiter(this, void 0, void 0, function* () {
            this.log.info(`enabling`);
            if (this.room !== undefined) {
                this.log.error(`enable: room not undefined`);
                this.disable();
            }
            this.room = room;
            const companyDirectories = this.room.$getCompanyDirectories();
            companyDirectories.$onChildAdded(this.onCompanyDirectoriesChanged);
            companyDirectories.$onChildChanged(this.onCompanyDirectoriesChanged);
            companyDirectories.$onChildRemoved(this.onCompanyDirectoriesChanged);
        });
    }
    findMatchingEntry(directory, entry) {
        return Object.keys(directory).find(entryId => this.directoryEntriesMatch(directory[entryId], entry));
    }
    getDirectoryEntryName(entry) {
        if (entry.displayName)
            return entry.displayName;
        if (entry.firstName) {
            if (entry.lastName) {
                return entry.firstName + ' ' + entry.lastName;
            }
            else {
                return entry.firstName;
            }
        }
        else if (entry.lastName) {
            return entry.lastName;
        }
        this.log.error(`directory entry doesn't have any name fields: ${JSON.stringify(entry)}`);
        return Sutl.generateFirebaseId();
    }
    merge(newValue, currValue) {
        if (currValue === undefined)
            currValue = {};
        if (newValue.companyDirectoriesChanged)
            currValue.companyDirectoriesChanged = true;
        if (newValue.equipmentDirectories) {
            if (!currValue.equipmentDirectories)
                currValue.equipmentDirectories = {};
            for (const [equipmentId, directory] of Object.entries(newValue.equipmentDirectories)) {
                currValue.equipmentDirectories[equipmentId] = directory;
                if (currValue.equipmentDirectoryEntries) {
                    delete currValue.equipmentDirectoryEntries[equipmentId];
                }
            }
        }
        if (newValue.equipmentDirectoryEntries) {
            if (!currValue.equipmentDirectoryEntries)
                currValue.equipmentDirectoryEntries = {};
            for (const [equipmentId, entries] of Object.entries(newValue.equipmentDirectoryEntries)) {
                if (!currValue.equipmentDirectoryEntries[equipmentId]) {
                    currValue.equipmentDirectoryEntries[equipmentId] = {};
                }
                for (const [entryId, entry] of Object.entries(entries)) {
                    if (entry) {
                        currValue.equipmentDirectoryEntries[equipmentId][entryId] = entry;
                    }
                    else {
                        delete currValue.equipmentDirectoryEntries[equipmentId][entryId];
                    }
                }
            }
        }
        return currValue;
    }
    mergeDirectoryEntry(existingEntry, newEntry) {
        if (existingEntry.department === undefined && newEntry.department !== undefined) {
            existingEntry.department = newEntry.department;
        }
        if (existingEntry.displayName === undefined && newEntry.displayName !== undefined) {
            existingEntry.displayName = newEntry.displayName;
        }
        if (existingEntry.firstName === undefined && newEntry.firstName !== undefined) {
            existingEntry.firstName = newEntry.firstName;
        }
        if (existingEntry.isRoom === undefined && newEntry.isRoom !== undefined) {
            existingEntry.isRoom = newEntry.isRoom;
        }
        if (existingEntry.lastName === undefined && newEntry.lastName !== undefined) {
            existingEntry.lastName = newEntry.lastName;
        }
        if (existingEntry.onDesktop === undefined && newEntry.onDesktop !== undefined) {
            existingEntry.onDesktop = newEntry.onDesktop;
        }
        if (existingEntry.onMobile === undefined && newEntry.onMobile !== undefined) {
            existingEntry.onMobile = newEntry.onMobile;
        }
        if (existingEntry.organization === undefined && newEntry.organization !== undefined) {
            existingEntry.organization = newEntry.organization;
        }
        if (existingEntry.picture === undefined && newEntry.picture !== undefined) {
            existingEntry.picture = newEntry.picture;
        }
        if (existingEntry.presence === undefined && newEntry.presence !== undefined) {
            existingEntry.presence = newEntry.presence;
        }
        if (existingEntry.department === undefined && newEntry.department !== undefined) {
            existingEntry.department = newEntry.department;
        }
        if (newEntry.contacts) {
            if (existingEntry.contacts === undefined) {
                existingEntry.contacts = Sutl.deepCopy(newEntry.contacts);
            }
            else {
                for (const nc of Object.values(newEntry.contacts)) {
                    let found = false;
                    for (const ec of Object.values(existingEntry.contacts)) {
                        if (ec.number === nc.number && ec.type === nc.type) {
                            found = true;
                            break;
                        }
                    }
                    if (!found) {
                        existingEntry.contacts[Sutl.generateFirebaseId()] = Sutl.deepCopy(nc);
                    }
                }
            }
        }
    }
    mergeEntryIntoDirectory(directory, newEntry, newEntryId) {
        let entryId = this.findMatchingEntry(directory, newEntry);
        if (!entryId) {
            directory[newEntryId] = Sutl.deepCopy(newEntry);
            entryId = newEntryId;
        }
        else {
            this.mergeDirectoryEntry(directory[entryId], newEntry);
        }
        return entryId;
    }
    onCompanyDirectoriesChanged() {
        this.update({ companyDirectoriesChanged: true });
    }
    updateEquipmentDirectory(equipmentId, entries, presenceEnabled = true) {
        if (!presenceEnabled && entries !== undefined) {
            for (const entry of Object.values(entries)) {
                delete entry.presence;
            }
        }
        const equipmentDirectories = {};
        equipmentDirectories[equipmentId] = entries !== null && entries !== void 0 ? entries : null;
        this.update({ equipmentDirectories });
    }
    updateEquipmentDirectoryEntry(equipmentId, entryId, entry, presenceEnabled = true) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        if (!presenceEnabled && entry !== undefined) {
            delete entry.presence;
            const currEntry = (_h = (_d = (_c = (_b = (_a = this.value) === null || _a === void 0 ? void 0 : _a.equipmentDirectoryEntries) === null || _b === void 0 ? void 0 : _b[equipmentId]) === null || _c === void 0 ? void 0 : _c[entryId]) !== null && _d !== void 0 ? _d : (_g = (_f = (_e = this.value) === null || _e === void 0 ? void 0 : _e.equipmentDirectories) === null || _f === void 0 ? void 0 : _f[equipmentId]) === null || _g === void 0 ? void 0 : _g[entryId]) !== null && _h !== void 0 ? _h : (_j = this.equipmentDirectories[equipmentId]) === null || _j === void 0 ? void 0 : _j[entryId];
            if (currEntry !== undefined && Sutl.objEquals(currEntry, entry))
                return;
        }
        const entries = {};
        entries[equipmentId] = {};
        entries[equipmentId][entryId] = entry !== null && entry !== void 0 ? entry : null;
        this.update({ equipmentDirectoryEntries: entries }, this.isDeferring ? undefined : 1000);
    }
}
